import { modelContributionQueryOptions } from '@/dashboards/ModelContributions/api';
import { TModelContributionRow } from '@/duckdb/model-contributions';
import { useExecutiveSummaryContext } from '@/dashboards/ExecutiveSummary/context';
import { useQuery } from '@tanstack/react-query';

const sumContribution = (rows: TModelContributionRow[]): number =>
    rows.reduce((acc, row) => acc + row.contribution, 0);

/**
 * Total KPI — sum of all contribution over the selected period.
 *
 * Reuses `modelContributionQueryOptions` (the same source the Model
 * Contributions dashboard reads from) once for the primary range
 * and once for the comparison range.
 */
export const useTotalKpiQuery = (): {
    isLoading: boolean;
    isFetching: boolean;
    data: {
        primary: number;
        comparison: number;
    };
} => {
    const { primaryRange, comparisonRange } = useExecutiveSummaryContext();

    const primaryQueryResults = useQuery(
        modelContributionQueryOptions({
            period: 'yearly',
            startDate: primaryRange.startDate,
            endDate: primaryRange.endDate,
            onlyMedia: false,
        }),
    );

    const comparisonQueryResults = useQuery(
        modelContributionQueryOptions({
            period: 'yearly',
            startDate: comparisonRange?.startDate,
            endDate: comparisonRange?.endDate,
            onlyMedia: false,
        }),
    );

    return {
        isLoading:
            primaryQueryResults.isLoading || comparisonQueryResults.isLoading,
        isFetching:
            primaryQueryResults.isFetching ||
            comparisonQueryResults.isFetching,
        data: {
            primary: sumContribution(primaryQueryResults.data?.rows ?? []),
            comparison: sumContribution(
                comparisonQueryResults.data?.rows ?? [],
            ),
        },
    };
};
